import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { cn } from '@/lib/infra/utils';
import { describeRecentView, recentViewsReturnState, useRecentGameViews } from './recentViews';

const MENU_WIDTH = 240; // matches w-[240px] below — keep in sync

/**
 * #1078 — Game Detail's Recent views menu. Choosing a game is the one entry
 * that resumes that game's latest reading view; every other way in starts
 * normally. It navigates with the Recent marker and nothing else: no ticker,
 * side or account, so it can never aim a card, seed a ticket or name an
 * account. The current game is not offered.
 *
 * The trigger is a fixed-width slot that renders in every state (disabled
 * when there is nothing to offer), so the list filling in never moves the
 * header. Items are buttons inside a `role="menu"`; the LT2 hotkey listener
 * treats both as key owners, so keys pressed while choosing never trade.
 *
 * Keyboard (the Source menu's pattern): the first game takes focus on open,
 * the arrows, Home and End walk the list, Escape closes and returns focus to
 * the trigger, and Tab leaves from the trigger rather than from the end of
 * the page where the portalled menu lives. The menu is placed once under the
 * trigger, so it closes when the page scrolls or resizes instead of floating
 * away from the button.
 */
export function RecentViewsMenu({ currentGameKey }: { currentGameKey: string | null }) {
  const navigate = useNavigate();
  const entries = useRecentGameViews().filter((e) => e.gameKey !== currentGameKey);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isOpen = open && entries.length > 0;

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useDismissibleLayer({
    open: isOpen,
    onDismiss: (reason) => close(reason === 'escape'),
    refs: [triggerRef, menuRef],
  });

  // Focus the first game once the menu is in the DOM.
  useEffect(() => {
    if (!isOpen) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [isOpen]);

  // Placed once, from the trigger's box at open: a scroll that moves the
  // trigger (the reading pane or the page) or a resize would leave it floating
  // over unrelated content, so either closes it. Capture phase, because a
  // scroll does not bubble. A scroller that does not hold the trigger (a book,
  // a feed, the menu itself) moves nothing under the menu and is ignored.
  useEffect(() => {
    if (!isOpen) return;
    const onScroll = (e: Event) => {
      const target = e.target;
      const movesTrigger = target === document
        || (target instanceof Node && triggerRef.current != null && target.contains(triggerRef.current));
      if (movesTrigger) setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => items[(to + items.length) % items.length]?.focus();
    if (e.key === 'ArrowDown') move(at + 1);
    else if (e.key === 'ArrowUp') move(at - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(items.length - 1);
    else if (e.key === 'Escape') close(true); // stopped below, so the layer hook never sees it
    else if (e.key === 'Tab') return close(true);
    else return;
    e.preventDefault();
  };

  const menu = isOpen && createPortal(
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      aria-label="Recent games"
      className="bg-card border-border fixed z-[100] w-[240px] rounded-lg border py-1 shadow-lg"
      style={{ top: position.top, left: position.left }}
      onKeyDown={(e) => {
        e.stopPropagation();
        onMenuKeyDown(e);
      }}
    >
      {entries.map((entry) => { const detail = describeRecentView(entry); return (
        <button
          key={entry.gameKey}
          type="button"
          role="menuitem"
          onClick={() => {
            setOpen(false);
            navigate(entry.path, { state: recentViewsReturnState() });
          }}
          className="hover:bg-muted focus:bg-muted flex w-full flex-col px-3 py-1.5 text-left outline-none transition-colors"
        >
          <span className="text-foreground truncate text-sm font-medium">{entry.title}</span>
          <span className="text-muted-foreground truncate text-[10px] uppercase tracking-wider">{entry.subtitle}</span>
          {/* Wraps rather than truncates: the label is the view's identity. */}
          {detail && <span className="text-foreground/80 break-words text-[11px] leading-snug">{detail}</span>}
        </button>
      ); })}
    </div>,
    document.body,
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (isOpen) return close(false);
          // Placed in the same update that opens it, so the first painted
          // frame is already under the trigger (an effect would paint at 0,0).
          const rect = triggerRef.current?.getBoundingClientRect();
          if (rect) setPosition({ top: rect.bottom + 4, left: Math.max(8, rect.right - MENU_WIDTH) });
          setOpen(true);
        }}
        disabled={entries.length === 0}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={entries.length === 0
          ? 'No other games viewed yet'
          : 'Return to a recent game as you left it. Opening a game any other way starts it fresh.'}
        className={cn(
          'flex w-24 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] leading-4 transition-colors',
          // On the page background (not a card), muted-foreground measures
          // 4.39:1 in light; /70 clears 4.5:1 in both themes.
          'text-foreground/70 hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:hover:bg-transparent',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70',
          isOpen && 'bg-muted text-foreground',
        )}
      >
        <History className="h-3 w-3" aria-hidden="true" />
        Recent{entries.length > 0 ? ` (${entries.length})` : ''}
      </button>
      {menu}
    </>
  );
}
